import {FormEvent, Fragment, useMemo, useState} from 'react';
import {useMutation} from 'services/apollo';
import {EzPage, EzCard, EzHeading, EzField, EzLink} from '@ezcater/recipe';
import {FlashMessages} from '@ezcater/flash-messages';
import gql from 'graphql-tag';

import {formatPhoneNumber} from 'util/StringUtils';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {usePageViewTracking} from 'util/tracking';
import {REQUEST_SIGN_IN_LINK} from 'util/tracking/trackingEvents';
import {
  type DriverSelfAssignSource,
  type RequestSignInAccountCreateMutation,
  type RequestSignInAccountCreateMutationVariables,
  type RequestSignInMagicLinkCreateMutation,
  type RequestSignInMagicLinkCreateMutationVariables,
} from 'services/graphql/types';
import Button from 'components/Button';
import {TERMS_URL} from 'paths';
import {getAdaptedSelfAssignSource} from 'util/urlUtils';
import {ErrorText} from 'styles/global.styles';
import Layout from '../Layout';
import RequestSignInLinkSuccess from './RequestSignInLinkSuccess';

export const AccountCreateMutation = gql(/* GraphQL */ `
  mutation RequestSignInAccountCreateMutation($input: DriverAccountSelfCreateInput!) {
    accountCreate: driverAccountSelfCreate(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

export const MagicLinkCreateMutation = gql(/* GraphQL */ `
  mutation RequestSignInMagicLinkCreateMutation($input: DriverAccountMagicLinkCreateInput!) {
    magicLinkCreate: driverAccountMagicLinkCreate(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

const getUrlParams = (
  search?: string
): {deliveryId?: string; source?: `${DriverSelfAssignSource}`} => {
  const parsedQueryString = new URLSearchParams(search);

  return {
    deliveryId: parsedQueryString.get('deliveryId') || undefined,
    source: getAdaptedSelfAssignSource(parsedQueryString.get('source')),
  };
};

const RequestSignInLink = () => {
  usePageViewTracking(REQUEST_SIGN_IN_LINK);
  const {search} = useLocation();
  const {t} = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');

  const {deliveryId, source} = useMemo(() => getUrlParams(search), [search]);

  const onChange = (value: string) => {
    setPhoneNumber(value);
  };

  const [requestInvitationQuery, requestInvitationData] = useMutation<
    RequestSignInAccountCreateMutation,
    RequestSignInAccountCreateMutationVariables
  >(AccountCreateMutation);

  const [magicLinkRequestQuery, magicLinkRequestData] = useMutation<
    RequestSignInMagicLinkCreateMutation,
    RequestSignInMagicLinkCreateMutationVariables
  >(MagicLinkCreateMutation);

  const requestInvitation = async () => {
    await requestInvitationQuery({
      variables: {
        input: {
          phone: phoneNumber,
          deliveryId,
        },
      },
    });
  };

  const requestMagicLink = async () => {
    await magicLinkRequestQuery({
      variables: {
        input: {
          phone: phoneNumber,
          deliveryId,
          source,
        },
      },
    });
  };

  const isQrCodeSelfAssign = !!(source && source === 'QRCODE' && deliveryId);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      if (isQrCodeSelfAssign) {
        await requestInvitation();
      } else {
        await requestMagicLink();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const defaultError = t('requestSignInLink.error');
  const title = t('requestSignInLink.title');
  const header = t('requestSignInLink.header');
  const subheader = t('requestSignInLink.subheader');
  const phoneNumberText = t('requestSignInLink.phoneNumber');
  const phoneNumberPlaceholder = t('requestSignInLink.phoneNumberPlaceholder');
  const buttonText = t('requestSignInLink.button');
  const disclaimer = t('requestSignInLink.disclaimer');
  const terms = t('requestSignInLink.terms');

  const requestInvitationUserErrors = requestInvitationData?.data?.accountCreate?.userErrors;
  const requestMagicLinkUserErrors = magicLinkRequestData?.data?.magicLinkCreate?.userErrors;
  const userErrors = requestInvitationUserErrors || requestMagicLinkUserErrors;
  const hasErrors =
    !!requestInvitationData.error || !!magicLinkRequestData.error || !!userErrors?.length;

  const isLoading = requestInvitationData.loading || magicLinkRequestData.loading;
  const requestComplete =
    !isLoading &&
    (!!requestInvitationData?.data?.accountCreate ||
      !!magicLinkRequestData?.data?.magicLinkCreate) &&
    !userErrors?.length;

  const showSuccessMessages = requestComplete && !hasErrors;

  return (
    <Layout title={title}>
      <EzPage>
        <FlashMessages />
        {showSuccessMessages && <RequestSignInLinkSuccess />}
        {!showSuccessMessages && (
          <>
            <EzHeading size={'3'}>{header}</EzHeading>
            <EzHeading size={'4'}>{subheader}</EzHeading>
            <form onSubmit={handleSubmit}>
              <EzCard>
                {(requestInvitationData.error || magicLinkRequestData.error) && (
                  <ErrorText>{defaultError}</ErrorText>
                )}
                {!!userErrors?.length && (
                  <ErrorText>
                    {userErrors.map((error, i) => (
                      <Fragment key={i}>
                        {error.message}
                        <br />
                      </Fragment>
                    ))}
                  </ErrorText>
                )}
                <>
                  <EzField
                    type="text"
                    label={phoneNumberText}
                    value={formatPhoneNumber(phoneNumber)}
                    onChange={e => onChange(e.currentTarget.value)}
                    placeholder={phoneNumberPlaceholder}
                  />
                  <Button
                    type="submit"
                    buttonType="primary"
                    buttonText={buttonText}
                    loading={isLoading}
                    disabled={phoneNumber === ''}
                  />
                  <EzHeading size="6">
                    {disclaimer}&nbsp;&nbsp;
                    <EzLink href={TERMS_URL} target="_blank">
                      {terms}
                    </EzLink>
                  </EzHeading>
                </>
              </EzCard>
            </form>
          </>
        )}
      </EzPage>
    </Layout>
  );
};

export default RequestSignInLink;
